import {
  MAX_PAYLOAD_SIZE,
  PUBLIC_VIEW_KEY_LENGTH,
} from "./../../constants/encryption";

import {
  CONVERSATION_ID_LENGTH,
  MESSAGE_LENGTH_PREFIX_LENGTH,
  SENDER_PROOF_LENGTH,
} from './../../constants/dataFrame';

/** Offset constants for fixed-layout message buffer */
const OFFSET_LENGTH_PREFIX = 0;
const OFFSET_CONVERSATION_ID = OFFSET_LENGTH_PREFIX + MESSAGE_LENGTH_PREFIX_LENGTH;
const OFFSET_PROOF = OFFSET_CONVERSATION_ID + CONVERSATION_ID_LENGTH;
const OFFSET_NEXT_VIEW_KEY = OFFSET_PROOF + SENDER_PROOF_LENGTH;
const OFFSET_VIEW_KEY = OFFSET_NEXT_VIEW_KEY + PUBLIC_VIEW_KEY_LENGTH;
const OFFSET_PAYLOAD = OFFSET_VIEW_KEY + PUBLIC_VIEW_KEY_LENGTH;

const HEADER_SIZE = OFFSET_PAYLOAD;

/**
 * Packs a serialized message fragment into a fixed-size buffer for transmission.
 *
 * This includes metadata such as:
 * - 2-byte payload length
 * - conversation ID
 * - sender HMAC proof
 * - current and next view public keys
 * - payload content
 *
 * To prevent payload length inference, the remaining unused buffer space
 * is filled with cryptographically secure random bytes.
 *
 * @param chunkPayload - The serialized payload content to send
 * @param conversationId - 16-byte unique identifier for the message thread
 * @param senderProof - 64-byte HMAC-based authenticity proof
 * @param senderViewPublicKey - Current view key used to encrypt/decrypt
 * @param senderNextViewPublicKey - Forward secrecy key for the next message
 * @param randomFill - Function to securely fill a Uint8Array with random data;
 *                     must be provided by the platform-specific environment (browser or Node)
 * @returns A MAX_PAYLOAD_SIZE buffer ready for transmission
 */
export function createDataFrame(
  chunkPayload: Uint8Array,
  conversationId: Uint8Array,
  senderProof: Uint8Array,
  senderViewPublicKey: Uint8Array,
  senderNextViewPublicKey: Uint8Array,
  randomFill: (target: Uint8Array) => void
): Uint8Array {
  const payloadLength = chunkPayload.length;

  if (conversationId.length !== CONVERSATION_ID_LENGTH) {
    throw new Error(`conversationId must be exactly ${CONVERSATION_ID_LENGTH} bytes`);
  }

  const maxAllowedPayloadSize = MAX_PAYLOAD_SIZE - HEADER_SIZE;
  if (payloadLength > maxAllowedPayloadSize) {
    throw new Error(`Payload too large: ${payloadLength} bytes (max ${maxAllowedPayloadSize})`);
  }

  const output = new Uint8Array(MAX_PAYLOAD_SIZE);

  // 2-byte big-endian length prefix
  output[OFFSET_LENGTH_PREFIX] = payloadLength >> 8;
  output[OFFSET_LENGTH_PREFIX + 1] = payloadLength & 0xff;

  output.set(conversationId, OFFSET_CONVERSATION_ID);
  output.set(senderProof, OFFSET_PROOF);
  output.set(senderNextViewPublicKey, OFFSET_NEXT_VIEW_KEY);
  output.set(senderViewPublicKey, OFFSET_VIEW_KEY);
  output.set(chunkPayload, OFFSET_PAYLOAD);

  // Fill remaining buffer space with secure random bytes
  randomFill(output.subarray(OFFSET_PAYLOAD + payloadLength));

  return output;
}